import { useState, useRef, ChangeEvent, useId } from 'react';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { Download, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface FileUploadProps {
  id?: string;
  name: string;
  label?: string;
  accept?: string;
  maxSizeMB?: number;
  error?: boolean;
  helperText?: string;
  value?: File | null;
  onChange?: (file: File | null) => void;
  className?: string;
  disabled?: boolean;
  /** Server-side file already saved — shown when `value` is empty */
  existingFileLabel?: string | null;
  existingHint?: string;
  onDownloadExisting?: () => void;
  downloadExistingLabel?: string;
  replaceFileLabel?: string;
}

export const FileUpload = ({
  id,
  name,
  label,
  accept,
  maxSizeMB = 10,
  error,
  helperText,
  value,
  onChange,
  className,
  disabled,
  existingFileLabel,
  existingHint,
  onDownloadExisting,
  downloadExistingLabel = 'Download',
  replaceFileLabel = 'Replace file',
}: FileUploadProps) => {
  const autoId = useId();
  const inputId = id ?? autoId;
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = (file: File | null) => {
    if (!file) {
      onChange?.(null);
      return;
    }

    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > maxSizeMB) {
      onChange?.(null);
      return;
    }

    onChange?.(file);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    handleFile(file);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleRemove = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    onChange?.(null);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className={cn('grid w-full items-center gap-3', className)}>
      {label && <Label htmlFor={inputId}>{label}</Label>}
      <div className="flex flex-col gap-1">
        <div
          className={cn(
            'relative flex flex-col items-center justify-center w-full border-2 border-dashed rounded-lg p-6 transition-colors',
            dragActive ? 'border-primary bg-primary/5' : 'border-gray-300 hover:border-gray-400',
            error && 'border-destructive',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            ref={inputRef}
            type="file"
            id={inputId}
            name={name}
            accept={accept}
            onChange={handleChange}
            disabled={disabled}
            className="hidden"
          />

          {value ? (
            <div className="flex items-center gap-3 w-full">
              <div className="flex-1 flex items-center gap-2">
                <Upload className="h-5 w-5 text-gray-500" />
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-gray-900">{value.name}</span>
                  <span className="text-xs text-gray-500">{formatFileSize(value.size)}</span>
                </div>
              </div>
              {!disabled && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={handleRemove}
                  className="h-8 w-8"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          ) : existingFileLabel ? (
            <div className="flex flex-col gap-3 w-full sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <Upload className="h-5 w-5 shrink-0 text-gray-500" />
                <div className="flex min-w-0 flex-col">
                  <span className="truncate text-sm font-medium text-gray-900">
                    {existingFileLabel}
                  </span>
                  {existingHint ? (
                    <span className="text-xs text-gray-500">{existingHint}</span>
                  ) : null}
                </div>
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                {onDownloadExisting && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    onClick={onDownloadExisting}
                  >
                    <Download className="h-4 w-4" />
                    {downloadExistingLabel}
                  </Button>
                )}
                {!disabled && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => inputRef.current?.click()}
                  >
                    {replaceFileLabel}
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Upload className="h-8 w-8 text-gray-400" />
              <div className="text-center">
                <Label
                  htmlFor={inputId}
                  className="text-sm font-medium text-gray-700 cursor-pointer hover:text-gray-900"
                >
                  Click to upload
                </Label>
                <p className="text-xs text-gray-500 mt-1">or drag and drop</p>
                <p className="text-xs text-gray-400 mt-1">Max size: {maxSizeMB}MB</p>
              </div>
            </div>
          )}
        </div>
        {error && helperText && <p className="text-red-500 text-xs">{helperText}</p>}
      </div>
    </div>
  );
};
